import { DOCUMENT } from '@angular/common';
// import { Response } from '@angular/http';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EnvironmentService } from 'app/core/services/environment.service';
import * as _ from 'lodash';
import moment from 'moment';
import * as diff from 'recursive-diff';
import { Subscription } from 'rxjs';
import { IIdentifiable } from '../../interfaces/identifiable.interface';
import { CONSTANTS } from './constants';

@Injectable()
export class UtilityService {
    public constructor(@Inject(DOCUMENT) private document: Document,
                       private environmentService: EnvironmentService,
    ) {
    }

    public isEUCountry = (countryCode: string): boolean => CONSTANTS.EUCountryCodes.includes(countryCode?.toUpperCase());

    public getSiteCountry(): string {
        const domainName = this.getCurrentDomainName();
        return _.findKey(this.environmentService.environment.sites, (item) => {
            return domainName.endsWith(item);
        }) || 'en';
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    public delay(fn: Function, delay: number = 300): any {
        return setTimeout(() => fn(), delay);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    public repeat(fn: Function, delay: number = 300): any {
        return setInterval(() => fn(), delay);
    }

    public isEmptyOrNullOrUndefined(values: any[]): boolean {
        if (!values || !values.length) {
            return false;
        }

        const booleanArray = values.map(value => value === null || typeof value === 'undefined' || value.trim() === '');
        return booleanArray.reduce((result, value) => result || value, false);
    }

    public isStringEmptyOrNullOrUndefined(value: string): boolean {
        return value === '' || value === null || typeof value === 'undefined';
    }

    public stripTags(text: string) {
        const regex = new RegExp(/<\/?\w+[^>]*\/?>/, 'g');
        return text.replace(regex, '');
    }

    public getGreetingTime(dateTime?: string | Date): string {
        const momentTime = dateTime ? moment(dateTime) : moment();
        if (!momentTime.isValid()) {
            return 'welcome';
        }

        const splitAfternoon = 12;
        const splitEvening = 17;
        const currentHour = parseFloat(momentTime.format('HH'));

        if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
            return 'greetings.afternoon';
        }

        if (currentHour >= splitEvening) {
            return 'greetings.evening';
        }

        return 'greetings.morning';
    }

    /**
     * Gets the 12-hour number of the given hour number.
     *
     * @param {number} _24HourTime
     * @returns {number}
     */
    public get12HourTime(_24HourTime: number) {
        if (_24HourTime <= 12) {
            return _24HourTime;
        }

        return _24HourTime - 12;
    }

    public getMinMoment(dateTimeStrings: string[]) {
        const moments = dateTimeStrings.map(item => moment(item).isValid() ? moment(item) : null)
                                       .filter(item => !!item);
        return moment.min(...moments);
    }

    public getMaxMoment(dateTimeStrings: string[]) {
        const moments = dateTimeStrings.map(item => moment(item).isValid() ? moment(item) : null)
                                       .filter(item => !!item);
        return moment.max(...moments);
    }

    /**
     * Based on https://stackoverflow.com/a/20392392/3797166
     *
     * @param jsonString
     * @returns {any}
     */
    public tryParseJSON(jsonString): any {
        try {
            const o = JSON.parse(jsonString);

            // Handle non-exception-throwing cases:
            // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
            // but... JSON.parse(null) returns null, and typeof null === "object",
            // so we must check for that, too. Thankfully, null is falsey, so this suffices:
            if (o && typeof o === 'object') {
                return o;
            }
        } catch (e) {
            return false;
        }
    }

    public tryParseResponseJSON<T>(response: Response | HttpResponse<T> | HttpErrorResponse): false | T | Promise<any> {
        try {
            if (response instanceof HttpResponse || response instanceof HttpErrorResponse) {
                return (<HttpResponse<T>>response).body;
            }

            return (<Response>response).json();
        } catch (e) {
            return false;
        }
    }

    /**
     * Gets the formatted number in currency format.
     *
     * @param {number} value The number to be formatted.
     * @param {string} currencyCode 'USD', 'EUR', 'CNY', etc.
     * @param {string} locale 'en-US', 'de-DE', etc.
     * @param {number} fractionDigits
     * @returns {string} The string formatted as money e.g. $1.00.
     */
    public formatMoney(value: number, currencyCode?: string, locale = 'en-US', fractionDigits = 2): string {
        const options: any = {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        };

        if (currencyCode) {
            options.style = 'currency';
            options.currency = currencyCode;
            options.currencyDisplay = 'symbol';
        }

        // [MV3-4840] business wants number format(period to comma) for GB similar to DE
        locale = (locale === 'en-GB') ? 'de-GB' : locale; // TODO: Need to find better solution

        // ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
        return new Intl.NumberFormat([locale, 'en-US'], options).format(value);
    }

    public clearSubscriptions(subscriptions: Subscription[]) {
        subscriptions.forEach(
            (subscription) => {
                if (!subscription) {
                    return true;
                }

                subscription.unsubscribe();
            },
        );

        subscriptions.fill(null);
    }

    public isObjectsDifferent<T>(obj1: T, obj2: T, isLogDiff = false) {
        const delta = this.getObjectsDifference(obj1, obj2);
        if (isLogDiff) {
            console.log(delta);
        }

        return !!Object.keys(delta).length;
    }

    public getObjectsDifference<T>(obj1: T, obj2: T): any {
        return diff.getDiff(obj1, obj2);
    }

    public truncateText(text: string, characterCount: number) {
        if (text.length <= characterCount) {
            return text;
        }

        return `${ text.substring(0, characterCount) }&hellip;`;
    }

    public isSimilarObjectsByProperties(object1: any, object2: any, props: string[]): boolean {
        return props.every(
            (prop) => {
                if (!Object.prototype.hasOwnProperty.call(object1, prop)
                    || !Object.prototype.hasOwnProperty.call(object2, prop)) {
                    return false;
                }

                const object1String = object1[prop].toString().trim().toUpperCase();
                const object2String = object2[prop].toString().trim().toUpperCase();

                return object1String === object2String;
            },
        );
    }

    /**
     *
     */
    public getDomain(): string {
        const domainName = this.getCurrentDomainName();
        return _.findKey(this.environmentService.environment.sites, (item) => {
            return domainName.endsWith(item);
        }) || 'en';
    }

    public getCurrentDomainName(): string {
        return this.document.location.hostname;
        // return 'upscapitalde.com';
    }

    public isEUDomainName(): boolean {
        const siteDomainKey = this.getDomain();
        return siteDomainKey !== 'en';
    }

    public getCookieCountryCode(siteLanguageCode: string): string {
        const cookieCountryCode = {
            en: 'US',
            de: 'DE',
            fr: 'FR',
            it: 'IT',
            ja: 'JP',
            es: 'MX',
            th: 'TH',
            'en-gb': 'GB',
            'zh-hk': 'HK',
        };

        return cookieCountryCode[siteLanguageCode?.toLowerCase()]?.toUpperCase() || siteLanguageCode?.toUpperCase();
    }

    public getCookieLanguageCode(siteLanguageCode: string): string {
        const cookieLanguageCode = {
            en: 'en',
            de: 'de',
            fr: 'fr',
            it: 'it',
            ja: 'ja',
            mx: 'es',
            th: 'th',
            'en-gb': 'en-GB',
            'zh-hk': 'zh',
        };

        return cookieLanguageCode[siteLanguageCode?.toLowerCase()] || siteLanguageCode;
    }

    public getPathName() {
        const pathName = window.location.pathname;
        switch (pathName.toLocaleLowerCase()) {
            case '/contactus/region/de': {
                return 'contactUs_DE';
                break;
            }
            case '/contactus/region/us': {
                return 'contactUs_US';
                break;
            }
            case '/contactus/region/uk': {
                return 'contactUs_UK';
                break;
            }
            case '/contactus/region/th': {
                return 'contactUs_TH';
                break;
            }
            case '/contactus/region/hk': {
                return 'contactUs_HK';
                break;
            }
            case '/contactus/region/jp': {
                return 'contactUs_JP';
                break;
            }
            case '/contactus/region/it': {
                return 'contactUs_IT';
                break;
            }
            case '/contactus/region/fr': {
                return 'contactUs_FR';
                break;
            }
            default: {
                break;
            }
        }

    }

    public getSiteLanguage(): string {
        // First, we get the language based on the site URL.
        const domainName = this.getCurrentDomainName();
        const siteLanguage = _.findKey(this.environmentService.environment.sites, (item) => {
            return domainName.endsWith(item);
        });

        // If a country is specified, we set the language based on the specified country.
        const searchParams = new URLSearchParams(window.location.search);
        let country = searchParams.get('region');
        if (!country) {
            const urlPath = window.location.pathname.toLowerCase();
            if (urlPath.startsWith('/contactus/region')) {
                country = urlPath.split('/').pop();
            }
        }

        const countryLanguage = {
            us: 'en',
            uk: 'en-gb',
            gb: 'en-gb',
            de: 'de',
            fr: 'fr',
            hk: 'zh-hk',
            it: 'it',
            jp: 'ja',
            mx: 'es',
            th: 'th',
        };

        const languageByCountry = country ? countryLanguage[country] : null;

        // If a language is explicitly specified, we use it.
        const specifiedLanguage = searchParams.get('language') || searchParams.get('lang');

        // We set the language by this order of priority.
        // console.log(specifiedLanguage, languageByCountry, siteLanguage);
        return specifiedLanguage || languageByCountry || siteLanguage || 'en';
    }

    public deepMerge(target: any, source: any): any {
        if (typeof target !== 'object' || typeof source !== 'object') {
            return source;
        }

        for (const key in source) {
            if (Object.prototype.hasOwnProperty.call(source, key)) {
                if (source[key] instanceof Array) {
                    // If it's an array, replace it entirely
                    target[key] = source[key].slice();
                } else if (typeof source[key] === 'object') {
                    // If it's an object, recurse
                    if (!target[key]) {
                        target[key] = {};
                    }
                    this.deepMerge(target[key], source[key]);
                } else {
                    // For primitive types, directly assign
                    target[key] = source[key];
                }
            }
        }

        return target;
    }

    /**
     * @deprecated Use EnumService instead.
     * True if the given value is a value of the specified Enum; otherwise, false.
     * @param enumObj
     * @param value
     */
    public isValueOfEnum<T>(enumObj: T, value: any): value is T[keyof T] {
        return Object.values(enumObj).includes(value);
    }

    /**
     * @deprecated Use EnumService instead.
     * Get the enum key string by the specified value.
     * @param enumObj
     * @param value
     */
    public getEnumKeyByValue<T extends { [index: string]: string | number }>(enumObj: T,
                                                                             value: string | number,
    ): keyof T | null {
        // Check if the value is actually a key in the enum
        if (typeof value === 'string' && value in enumObj) {
            return value as keyof T;
        }

        // Otherwise, try to find the key by the value
        for (const [key, val] of Object.entries(enumObj)) {
            if (val === value) {
                return key as keyof T;
            }
        }

        return null;
    }

    /**
     * @deprecated Use EnumService instead.
     * Get enum value by the specified key.
     *
     * @param enumType
     * @param key
     */
    public getEnumByKey<T extends object>(enumType: T, key: string | number): T[keyof T] | undefined {
        if (key in enumType) {
            return enumType[key as keyof T];
        }

        return undefined;
    }

    /**
     * @deprecated Use EnumService instead.
     * @param {T} enumType
     * @param {string | number} value
     * @returns {T[keyof T]}
     */
    public getEnumByValue<T>(enumType: T, value: string | number): T[keyof T] | undefined {
        return this.safeCastToEnum(enumType, value);
    }

    /**
     * @deprecated Use EnumService instead.
     * @param {T} enumType
     * @param {string | number} value
     * @returns {T[keyof T]}
     */
    public safeCastToEnum<T>(enumType: T, value: string | number): T[keyof T] | undefined {
        return Object.values(enumType).includes(value) ? (value as T[keyof T]) : undefined;
    }

    /**
     * @deprecated Use EnumService instead.
     * @param {T} enumType
     * @param {string | number} value
     * @returns {string}
     */
    public getEnumDefinitionFromKey<T extends Record<string, string | number>>(enumType: T, value: string | number): string {
        const enumKey = this.getEnumKeyByValue(enumType, value);
        if (!enumKey) {
            return value?.toString();
        }

        const enumKeyString = String(enumKey);
        const definition = enumKeyString
            .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase/PascalCase
            .replace(/[_-]/g, ' ')               // snake_case / kebab-case
            .trim();
        return this.toTitleCase(definition);
    }

    public isLocalhost(): boolean {
        return window.location.hostname.toLowerCase() === 'localhost';
    }

    public normalizeDomain(domain: string): string {
        return domain.replace(/^www\./, '');
    }

    /**
     * This function iterates over the entries of the given object, filters out the ones with null or empty string values,
     * and returns a new object that only includes the non-null fields.
     * The use of Partial<T> as the return type indicates that the function may return an object with a subset of the original object's properties.
     */
    public filterObjectFields<T>(obj: T): Partial<T> {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== null && value !== '') {
                acc[key as keyof T] = value;
            }
            return acc;
        }, {} as Partial<T>);
    }

    /**
     * @deprecated Use EnumService instead.
     * Transform a numeric enum into an array of identifiable objects.
     *
     * This function filters, sorts, and maps the values of a numeric enum to a more usable format,
     * returning an array of objects where each object contains the enum value as `id` and the corresponding
     * enum key as `name`. The function is designed to handle only numeric enums, filtering out reverse
     * mappings and skipping specified enum values.
     *
     * @template T - A numeric type representing the enum values.
     *
     * @param enumType - The enum to be transformed, provided as an object where the keys are the enum names and values are numeric.
     * @param enumSort - An optional array that specifies the desired order of the enum values. If provided, the resulting array will be sorted according to this order.
     * @param skipValues - An optional array of enum values to be excluded from the result. Any values in this array will not appear in the returned list.
     *
     * @returns An array of objects where each object has an `id` property corresponding to the enum value and a `name` property corresponding to the enum key.
     *
     * @example
     * enum OrderCategory {
     *   UNSPECIFIED = 0,
     *   ELECTRONICS = 1,
     *   BOOKS = 2,
     *   FASHION = 3,
     * }
     *
     * const categoriesSort = [OrderCategory.FASHION, OrderCategory.BOOKS, OrderCategory.ELECTRONICS];
     * const skipValues = [OrderCategory.UNSPECIFIED];
     *
     * const result = transformEnumToIdentifiable(OrderCategory, categoriesSort, skipValues);
     * console.log(result);
     * // Output:
     * // [
     * //   { id: 3, name: 'FASHION' },
     * //   { id: 2, name: 'BOOKS' },
     * //   { id: 1, name: 'ELECTRONICS' }
     * // ]
     */
    public transformEnumToIdentifiables<T extends string | number>(
        enumType: { [key: string]: T },
        enumSort: T[] = [],
        skipValues: T[] = [],
    ): IIdentifiable[] {
        const filteredTypes: T[] =
            Object.keys(enumType)
                  .filter(key => isNaN(Number(key))) // Filter out reverse mapping for numeric enums.
                  .filter(key => !skipValues.includes(enumType[key as keyof typeof enumType]))
                  .map(key => enumType[key as keyof typeof enumType]);

        const sortedTypes = filteredTypes || [];
        sortedTypes.sort((a: T, b: T) => {
            return enumSort.indexOf(a) - enumSort.indexOf(b);
        });

        return sortedTypes.map(item => ({
            id: item,
            name: Object.keys(enumType).find(key => enumType[key as keyof typeof enumType] === item) || '',
        }));
    }

    /**
     * @deprecated Use EnumService instead.
     * Transforms an array of enum values into an object where each key is the name of the enum member in uppercase,
     * and the value is the corresponding enum value.
     *
     * @template T - The enum type.
     * @param {T} enumType - The enum type to transform.
     * @param {T[keyof T][]} enumArray - An array of enum values to be transformed.
     * @returns {{ [key: string]: number }} - An object where the keys are the uppercase names of the enum members,
     * and the values are the corresponding enum values.
     *
     * @example
     * enum Category {
     *   Order = 1,
     *   Complete = 2,
     *   Cancelled = 3
     * }
     *
     * const categoryArray: Category[] = [Category.Order, Category.Complete];
     * const result = transformEnumArrayToObject(Category, categoryArray);
     * // result: { ORDER: 1, COMPLETE: 2 }
     *
     * @example
     * enum Status {
     *   Pending = 10,
     *   Approved = 20,
     *   Rejected = 30
     * }
     *
     * const statusArray: Status[] = [Status.Pending, Status.Approved];
     * const result = transformEnumArrayToObject(Status, statusArray);
     * // result: { PENDING: 10, APPROVED: 20 }
     */
    public transformEnumArrayToObject<T>(enumType: T, enumArray: T[keyof T][]): { [key: string]: number } {
        return enumArray.reduce((acc, enumValue) => {
            // Get the enum key (name) corresponding to the enum value
            const enumKey = Object.keys(enumType).find(key => enumType[key as keyof T] === enumValue);

            if (enumKey) {
                // Add the uppercase key to the accumulator object with its corresponding value
                acc[enumKey.toUpperCase()] = enumValue as unknown as number;
            }

            return acc;
        }, {} as { [key: string]: number });
    }

    public isNullOrEmpty(value: unknown): boolean {
        if (typeof value !== 'string') {
            return value === null;
        }

        return value?.trim() === '';
    }

    /**
     * @deprecated Use ObjectService.removeNullish() instead.
     * @param {T} obj
     * @returns {Partial<T>}
     */
    public removeNullish<T>(obj: T): Partial<T> {
        return Object.fromEntries(
            Object.entries(obj as Record<string, unknown>)
                  .filter(([_, v]) =>
                      v !== null
                      && v !== undefined
                      && !Number.isNaN(v),
                  ),
        ) as Partial<T>;
    }

    // public removeNullish(obj: Record<string, unknown>): Record<string, unknown> {
    //     return Object.fromEntries(Object.entries(obj)
    //                                     .filter(([_, v]) => v !== null && v !== undefined));
    // }

    /**
     * @deprecated Use ObjectService.assignIfNullOrUndefined() instead.
     * @param target
     * @param source
     * @returns {{[p: string]: unknown}}
     */
    public assignIfNullOrUndefined = (target: unknown, source: unknown): { [k: string]: unknown } =>
        Object.assign(target, Object.fromEntries(Object.entries(source).filter(([k, v]) => target[k] === null || target[k] === undefined)));

    public isPotentialFormula(input: unknown): boolean {
        return typeof input === 'string' && /^[=+\-@]/.test(input);
    }

    public escapeLeadingCharacters<T>(data: T[], charsToEscape: string[] = ['+', '-', '=', '@'], prefix: string = ' '): T[] {
        const pattern = new RegExp(`^[${charsToEscape.map(c => `\\${c}`).join('')}]`);

        const escapeValue = (value: unknown): unknown => {
            if (typeof value === 'string' && pattern.test(value)) {
                return prefix + value;
            }

            if (Array.isArray(value)) {
                return value.map(escapeValue);
            }

            if (typeof value === 'object' && value !== null) {
                const escaped: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(value)) {
                    escaped[k] = escapeValue(v);
                }
                return escaped;
            }

            return value;
        };

        return data.map(escapeValue) as T[];
    }

    /**
     * Generates a human-readable label from a given key by transforming its format.
     * Optionally allows overriding labels through a provided mapping.
     *
     * @param {string} key - The input key to be transformed into a label. It can use dot notation, snake_case, or kebab-case.
     * @param {Record<string, string>} [overrideLabels] - An optional object containing specific labels to override the generated ones.
     * @return {string} A human-readable label derived from the input key or the overridden label if provided.
     */
    public autoLabelFromKey(key: string, overrideLabels?: Record<string, string>): string {
        if (!key) {
            return '';
        }

        if (overrideLabels?.[key]) {
            return overrideLabels[key];
        }

        return key
            .split('.')
            .map(part =>
                part
                    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase/PascalCase
                    .replace(/[_-]/g, ' ')     // handle snake-case or kebab-case
                    .trim(),
            )
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    private toTitleCase(input: string): string {
        return input
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}
